/**
 * Canonical UI labels for demand lifecycle vocabulary.
 *
 * Single source of truth for human-readable labels — every surface
 * that renders a stage name, resolution type, or priority tier should
 * import from this file rather than defining its own map inline.
 *
 * Reason: when the substrate enum value disagrees with the user-facing
 * label (notably `not_prioritized` ↔ "Parking Lot"), every inline map
 * is a future bug. Issue #772 was caused by the Timeline tab missing
 * a translation that other surfaces had. Centralizing the maps stops
 * that class of bug at the source.
 *
 * THIS FILE IS THE REFERENCE. It used to point at `docs/terminology.md` for
 * that role; that document was a v2.0.1 snapshot still presenting the retired
 * stage model as canonical, and it has been deleted. The substrate side of the
 * vocabulary lives in `backend-python/models/demand.py`.
 */

import type { DemandStage } from '../types/demand'
import type { ApplicationStatus } from '../types/application'
import { bucketColors } from '../theme'

/**
 * Application status → label. Exhaustive over `ApplicationStatus` on purpose.
 *
 * Three separate surfaces spelled these out by hand as ternary chains, and two
 * of them let `withdrawn` fall into the default branch and reported it as
 * "Pending" — so council chased a candidate who had withdrawn, and the talent
 * was told their own withdrawn application was under review. A record the
 * compiler checks means the next status added cannot be silently swallowed.
 */
export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

/** DemandStage enum value → human-readable label. */
export const STAGE_LABELS: Record<DemandStage, string> = {
  new: 'New',
  under_review: 'Under Review',
  open_to_application: 'Open to Application',
  staffed: 'Staffed',
  resolved: 'Resolved',
  // DRIFT: substrate value is `not_prioritized`, display is "Parking Lot".
  // Every surface MUST go through this map; rendering the raw enum will
  // leak the old name.
  not_prioritized: 'Parking Lot',
  // New-model stages (FE expand, Inc-3) — so a migrated demand renders a real
  // label instead of leaking the raw value.
  scoping: 'Scoping',
  triaging: 'Triaging',
  resourcing: 'Resourcing',
  // Same DRIFT as `not_prioritized` above: substrate value is `parking`,
  // display is "Parking Lot" — matching the priority bucket of the same name,
  // which is the same concept (#988 §7). The v2.8 stage rename dropped the
  // "Lot" and reintroduced the very inconsistency this map exists to hide.
  parking: 'Parking Lot',
  closed: 'Closed',
}

/**
 * Which stages a user can actually be in TODAY, and which only survive to
 * render history.
 *
 * `STAGE_LABELS` deliberately contains both, because `demand_stage_history`
 * was never migrated off the old vocabulary and the timeline still has to
 * render those rows. That makes the map useless for answering "what are the
 * stages?" — which is a question documentation asks constantly, and got wrong:
 * the in-app guide described the retired set for months after the rename.
 *
 * Split out so there is one answer, and a test can hold the guide to it.
 */
export const LIVE_STAGES: DemandStage[] = [
  'new',
  'scoping',
  'triaging',
  'resourcing',
  'parking',
  'closed',
]

/** Read-only survivors. Never a target; kept so old timeline rows render. */
export const LEGACY_STAGES: DemandStage[] = [
  'under_review',
  'open_to_application',
  'staffed',
  'resolved',
  'not_prioritized',
]

/**
 * The stages in which a demand is still taking part in resourcing — the client
 * mirror of the backend's `raw_stages_for("resourcing")`.
 *
 * **Three values, not one.** `resourcing` plus the two legacy stages that alias
 * to it. Call sites kept writing `stage !== 'resourcing'` and were therefore
 * wrong for any row still carrying a legacy value: the v2.8.38 cutover remapped
 * production, so nothing is failing today, but the backend still dual-reads all
 * three and a client that disagrees with it is a bug waiting for one legacy row.
 *
 * Use this rather than comparing stage strings, so there is one definition of
 * the band on this side too.
 */
const RESOURCING_BAND: ReadonlySet<string> = new Set([
  'resourcing',
  'open_to_application',
  'staffed',
])

export function isInResourcingBand(stage?: string | null): boolean {
  return !!stage && RESOURCING_BAND.has(stage)
}

// RETIRED_STAGE_LABELS is defined below the closure maps, because it subtracts
// them and a const cannot be read before its initialiser runs.

/**
 * Closure LEAF value → label. Record<string,string> (not exhaustive) so a
 * not-yet-migrated legacy value still renders via resolutionTypeLabel().
 */
export const RESOLUTION_TYPE_LABELS: Record<string, string> = {
  promoted: 'Promoted',
  rejected: 'Rejected',
  merged: 'Merged',
  withdrawal: 'Withdrawn',
  // The three ways a demand ends up Resolved. `satisfied` keeps its stored
  // value — it is what production rows already carry — but reads as the
  // catch-all it is, so the three sit together in the UI.
  resolved_staffed: 'Resolved · Staffed',
  resolved_self_supported: 'Resolved · Self-supported',
  satisfied: 'Resolved · Other',
}

/** Demand type + support mode labels (v2.8.0 slice 3). */
export const DEMAND_TYPE_LABELS: Record<string, string> = {
  new_demand: 'New Demand',
  change_request: 'Change Request',
}
export const SUPPORT_MODE_LABELS: Record<string, string> = {
  council_supported: 'Council-Supported',
  self_supported: 'Self-Supported',
}

/** Derived top-level closure_type → label. */
export const CLOSURE_TYPE_LABELS: Record<string, string> = {
  promoted: 'Promoted',
  resolved: 'Resolved',
  rejected: 'Rejected',
  merged: 'Merged',
  withdrawn: 'Withdrawn',
}

/** Resolved-only subtype → label. */
export const CLOSURE_SUBTYPE_LABELS: Record<string, string> = {
  staffed: 'Staffed',
  self_supported: 'Self-supported',
  other: 'Other',
}

/**
 * Labels that mean ONLY a retired stage — the phrases that are simply wrong in
 * running prose today.
 *
 * Computed by subtraction, not listed, because three separate collisions would
 * make a hand-written list wrong:
 *
 *   "Parking Lot"  legacy `not_prioritized` AND live `parking`
 *   "Staffed"      legacy stage AND the live Resolved subtype
 *   "Resolved"     legacy stage AND the live top-level closure type
 *
 * Subtracting the live stage and closure vocabularies leaves only the phrases
 * with no current meaning anywhere. It also maintains itself: if a closure name
 * is ever retired, the colliding stage label rejoins this list without anyone
 * remembering to add it.
 */
export const RETIRED_STAGE_LABELS: string[] = LEGACY_STAGES.map((s) => STAGE_LABELS[s]).filter(
  (label) =>
    !LIVE_STAGES.some((s) => STAGE_LABELS[s] === label) &&
    !Object.values(CLOSURE_TYPE_LABELS).includes(label) &&
    !Object.values(CLOSURE_SUBTYPE_LABELS).includes(label),
)

/**
 * Leaf → derived top-level closure_type.
 *
 * MUST agree with `closure_type()` in backend-python/models/demand.py — a test
 * pins the two together, because this one exists only so a list can group
 * without waiting for the server, and a client that groups differently from
 * the server is worse than one that waits.
 *
 * Unknown values return null rather than a catch-all: there is no `other`
 * bucket any more, and filing an unrecognised leaf under one would report it
 * as an outcome somebody chose.
 */
export function closureTypeFor(resolution: string | null | undefined): string | null {
  if (!resolution) return null
  switch (resolution) {
    case 'promoted':
      return 'promoted'
    case 'rejected':
    case 'declined': // legacy
      return 'rejected'
    case 'merged':
      return 'merged'
    case 'withdrawal':
    case 'withdrawn': // legacy
      return 'withdrawn'
    case 'resolved_staffed':
    case 'resolved_self_supported':
    case 'satisfied':
    case 'completed': // legacy
    case 'deferred': // legacy
      return 'resolved'
    default:
      return null
  }
}

/** Leaf → the Resolved subtype, or null for every other closure type. */
export function closureSubtypeFor(resolution: string | null | undefined): string | null {
  switch (resolution) {
    case 'resolved_staffed':
      return 'staffed'
    case 'resolved_self_supported':
      return 'self_supported'
    case 'satisfied':
    case 'completed': // legacy
    case 'deferred': // legacy
      return 'other'
    default:
      return null
  }
}

/** Leaf → does the work continue elsewhere (promoted / merged)? */
export function isContinuing(resolution: string | null | undefined): boolean {
  return resolution === 'promoted' || resolution === 'merged'
}

/**
 * Priority bucket slug (kebab-case, as the server sends it) → label.
 *
 * The server derives the bucket and puts it on `demand.priority_bucket`
 * (`effective_priority_bucket` in models/demand.py). Read that; do not
 * re-derive from `priority_tier_override`. Both DemandList and DemandKanban
 * used to carry their own snake_case copy of this map plus their own
 * derivation, and the copies disagreed with the server: neither produced
 * Parking Lot for a parked demand, and both echoed a stale stored
 * `parking-lot` that the server deliberately ignores.
 *
 * `rejected` is the canonical slug for a demand closed as Rejected — the same
 * word the closure vocabulary uses (CLOSURE_TYPE_LABELS.rejected). `declined`
 * is kept because it is what legacy rows store, and dropping it would render
 * the raw slug.
 */
export const PRIORITY_TIER_LABELS: Record<string, string> = {
  'major-unlocks': 'Major Unlocks',
  'high-priority': 'High Priority',
  'quick-wins': 'Quick Wins',
  'parking-lot': 'Parking Lot',
  rejected: 'Rejected',
  declined: 'Declined', // legacy stored value
}

/** The buckets a user can filter by, in display order. */
export const PRIORITY_BUCKETS: string[] = [
  'major-unlocks',
  'high-priority',
  'quick-wins',
  'parking-lot',
  'rejected',
]

/**
 * Bucket slug → chip colour. The hexes live in `theme.ts` (mirrored by the
 * `--bucket-*` custom properties in index.css); this is only the slug mapping,
 * which both list surfaces previously kept their own snake_case copy of.
 *
 * `rejected` and `declined` share a colour deliberately — they are the same
 * bucket under two spellings, one current and one legacy.
 */
export const PRIORITY_BUCKET_COLORS: Record<string, string> = {
  'major-unlocks': bucketColors.majorUnlocks,
  'high-priority': bucketColors.highPriority,
  'quick-wins': bucketColors.quickWins,
  'parking-lot': bucketColors.parkingLot,
  rejected: bucketColors.declined,
  declined: bucketColors.declined,
}

/** Convenience: chip colour for a bucket slug, or null when it has none. */
export function priorityBucketColor(bucket: string | null | undefined): string | null {
  if (!bucket) return null
  return PRIORITY_BUCKET_COLORS[bucket] ?? null
}

/** ProductGateStatus enum value → human-readable label. */
export const GATE_STATUS_LABELS: Record<string, string> = {
  on_track: 'On Track',
  due: 'Due',
  overdue: 'Overdue',
}

/** Convenience: lookup a gate-status label safely. */
export function gateStatusLabel(status: string | null | undefined): string {
  if (!status) return ''
  return GATE_STATUS_LABELS[status] ?? status
}

/** Convenience: lookup a stage label safely (raw enum value passes through if not found). */
export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return ''
  return STAGE_LABELS[stage as DemandStage] ?? stage
}

/** Convenience: lookup a resolution-type label safely. */
export function resolutionTypeLabel(type: string | null | undefined): string {
  if (!type) return ''
  return RESOLUTION_TYPE_LABELS[type] ?? type
}

/** Convenience: lookup a priority-tier label safely. */
export function priorityTierLabel(tier: string | null | undefined): string {
  if (!tier) return ''
  return PRIORITY_TIER_LABELS[tier] ?? tier
}

/** Several requestors arrive from intake as one `;`-joined field. */
export function requestorNames(raw: string | null | undefined): string {
  return (raw ?? '').split(';').map((s) => s.trim()).filter(Boolean).join(', ')
}

/** What a demand tells talent before they apply (#1499), in reading order. */
export const PITCH_LABELS = {
  pitch_project: 'The project',
  pitch_impact: 'Why it matters',
} as const
